import { useEffect, useState } from 'react'
import { Link, useLocation } from 'wouter'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ArrowLeft, Save } from 'lucide-react'
import { toast } from 'sonner'
import { api } from '@/lib/api'
import { handleApiError } from '@/lib/api-error'
import type { AdminEmpresa, AdminPlan } from '@/types/admin'

const schema = z.object({
  empresa: z.object({
    rut: z.string().min(1, 'RUT requerido'),
    nombre_fantasia: z.string().min(1, 'Nombre requerido'),
    razon_social: z.string().optional(),
    email: z.string().email('Email inválido').optional().or(z.literal('')),
    telefono: z.string().optional(),
  }),
  admin: z.object({
    name: z.string().min(1, 'Nombre del admin requerido'),
    email: z.string().email('Email inválido'),
    rut: z.string().optional(),
  }),
  plan_id: z.string().optional(),
  cantidad_cupos: z.string().optional(),
})

type FormValues = z.infer<typeof schema>

interface CreateResponse {
  data: AdminEmpresa
}

export function EmpresaNuevaPage() {
  const [, navigate] = useLocation()
  const [planes, setPlanes] = useState<AdminPlan[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      empresa: { rut: '', nombre_fantasia: '', razon_social: '', email: '', telefono: '' },
      admin: { name: '', email: '', rut: '' },
      plan_id: '',
      cantidad_cupos: '',
    },
  })

  useEffect(() => {
    let cancelled = false
    api
      .get<{ data: AdminPlan[] }>('/admin/planes')
      .then((res) => {
        if (!cancelled) setPlanes(res.data ?? [])
      })
      .catch((err) => {
        if (!cancelled) handleApiError(err, 'No se pudieron cargar los planes.')
      })
    return () => {
      cancelled = true
    }
  }, [])

  const onSubmit = async (values: FormValues) => {
    setIsSubmitting(true)
    try {
      const payload: Record<string, unknown> = {
        empresa: {
          rut: values.empresa.rut,
          nombre_fantasia: values.empresa.nombre_fantasia,
          ...(values.empresa.razon_social ? { razon_social: values.empresa.razon_social } : {}),
          ...(values.empresa.email ? { email: values.empresa.email } : {}),
          ...(values.empresa.telefono ? { telefono: values.empresa.telefono } : {}),
        },
        admin: {
          name: values.admin.name,
          email: values.admin.email,
          ...(values.admin.rut ? { rut: values.admin.rut } : {}),
        },
      }
      if (values.plan_id) payload.plan_id = Number(values.plan_id)
      if (values.cantidad_cupos) payload.cantidad_cupos = Number(values.cantidad_cupos)

      const res = await api.post<CreateResponse>('/admin/empresas', payload)
      toast.success('Empresa creada correctamente.')
      navigate(`/empresas/${res.data.id}`)
    } catch (err) {
      handleApiError(err, 'No se pudo crear la empresa.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="max-w-3xl space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" asChild>
          <Link href="/empresas">
            <ArrowLeft className="mr-2 h-4 w-4" /> Volver
          </Link>
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Nueva empresa</h1>
          <p className="text-muted-foreground mt-1">Crear una empresa cliente y su administrador</p>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Datos de la empresa</CardTitle>
              <CardDescription>Información legal y de contacto</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="empresa.rut"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>RUT</FormLabel>
                    <FormControl>
                      <Input placeholder="76.123.456-7" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="empresa.nombre_fantasia"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nombre</FormLabel>
                    <FormControl>
                      <Input placeholder="Constructora Andes" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="empresa.razon_social"
                render={({ field }) => (
                  <FormItem className="sm:col-span-2">
                    <FormLabel>Razón social</FormLabel>
                    <FormControl>
                      <Input placeholder="Constructora Andes SpA" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="empresa.email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="contacto@empresa.cl" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="empresa.telefono"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Teléfono</FormLabel>
                    <FormControl>
                      <Input placeholder="+56 9 1234 5678" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Administrador inicial</CardTitle>
              <CardDescription>Usuario con acceso completo a la empresa</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="admin.name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nombre completo</FormLabel>
                    <FormControl>
                      <Input placeholder="Juan Pérez" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="admin.email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="juan@empresa.cl" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="admin.rut"
                render={({ field }) => (
                  <FormItem className="sm:col-span-2">
                    <FormLabel>RUT (opcional)</FormLabel>
                    <FormControl>
                      <Input placeholder="12.345.678-9" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Suscripción inicial</CardTitle>
              <CardDescription>Opcional — se puede asignar más adelante</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="plan_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Plan</FormLabel>
                    <Select value={field.value ?? ''} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar plan…" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {planes.map((p) => (
                          <SelectItem key={p.id} value={String(p.id)}>
                            {p.nombre}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="cantidad_cupos"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Cantidad de cupos (override)</FormLabel>
                    <FormControl>
                      <Input type="number" min={0} placeholder="Usar el del plan" {...field} />
                    </FormControl>
                    <FormDescription>Dejar vacío para usar el valor del plan.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" asChild>
              <Link href="/empresas">Cancelar</Link>
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              <Save className="mr-2 h-4 w-4" />
              {isSubmitting ? 'Creando…' : 'Crear empresa'}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  )
}
